/**
 * app.ts - Wires the framework-agnostic store to the game layer.
 *
 * This replaces what `vue-app.js` used to do: hold the HUD state, expose it to the UI, and
 * provide the two game commands the UI can trigger (`resetGame`, `toggleView`). The initial
 * values and the reset order mirror `vue-app.js` exactly.
 */

import { loadGame } from './game-bridge';
import { isReplayMode } from './dev/replay-source';
import type { GameHost, StageId, SkinId } from '../game/types.js';
import { DEFAULT_SKIN, skinSummaries } from '../game/car-skins.js';
import { createStore, type Store } from './state';

const KNOCKABLE_BOX_COUNT = 5;
import { NO_DIRECTION } from '../game/math/input-mapping.js';

const emptyBoxes = () => Array.from({ length: KNOCKABLE_BOX_COUNT }, () => ({ knocked: false }));

/** Same shape and initial values as `vue-app.js` `data()`. */
export function createInitialGameState(): GameHost {
    return {
        fps: 0,
        speed: 0,
        position: { x: 0, y: 0, z: 0 },
        rotation: 0,
        maxSpeed: 0,
        direction: NO_DIRECTION,
        collisions: 0,
        knockedBoxes: 0,
        raceTime: 0,
        isRacing: false,
        isTouchDevice: false,
        touchControls: { forward: false, backward: false, left: false, right: false, brake: false, jump: false },
        boxesStatus: emptyBoxes(),
        vehicle: {
            speedMps: 0,
            speedKmh: 0,
            gear: 'N',
            powerKw: 0,
            accelLongitudinal: 0,
            accelLateral: 0,
            yawRate: 0,
            steerAngleDeg: 0,
            collision: false
        },
        perf: {
            interval: { samples: 0, min: 0, p50: 0, p95: 0, p99: 0, max: 0, mean: 0 },
            script: { samples: 0, min: 0, p50: 0, p95: 0, p99: 0, max: 0, mean: 0 },
            fps: 0,
            physicsStepsLastFrame: 0,
            physicsStepsTotal: 0,
            drawCalls: 0,
            triangles: 0,
            geometries: 0,
            textures: 0,
            programs: 0,
            adasTargets: 0,
            updatedAt: 0
        },
        adasMode: 'off',
        adasAvailable: false,
        stage: 'studio',
        stages: [],
        setStage: () => undefined,
        shell: DEFAULT_SKIN,
        skins: skinSummaries(),
        setShell: () => undefined,
        runtime: { phase: 'loading', message: '', detail: '' },
        resetGame: () => undefined,
        toggleView: () => undefined,
        retry: () => undefined,
    };
}

/** Same field resets as `vue-app.js` `resetGame()`, before the session rebuild. */
function resetHostFields(host: GameHost): void {
    host.speed = 0;
    host.position = { x: 0, y: 0, z: 0 };
    host.rotation = 0;
    host.direction = NO_DIRECTION;
    host.collisions = 0;
    host.knockedBoxes = 0;
    host.maxSpeed = 0;
    host.raceTime = 0;
    host.isRacing = false;
    host.boxesStatus = emptyBoxes();
}

export interface GameHostRef {
    store: Store<GameHost>;
    host: GameHost;
}

export function createGameHost(): GameHostRef {
    const store = createStore<GameHost>(createInitialGameState());
    const host = store.state;

    host.resetGame = () => {
        resetHostFields(host);
        // The game chunk may still be loading; the call is queued behind it.
        void loadGame()
            .then(game => game.resetGame(host))
            .catch((error: unknown) => {
                console.error('❌ Game reset failed:', error);
            });
    };
    host.toggleView = () => {
        void loadGame().then(game => game.toggleView());
    };
    host.setStage = (stage: StageId) => {
        if (stage === host.stage) {
            return;
        }
        // Optimistic UI: the switcher highlights immediately; the game rebuilds the session.
        host.stage = stage;
        if (isReplayMode()) {
            return;
        }
        void loadGame()
            .then(game => game.setStage(host, stage))
            .catch((error: unknown) => {
                console.error('❌ Stage switch failed:', error);
            });
    };
    host.setShell = (shell: SkinId) => {
        if (shell === host.shell) {
            return;
        }
        // Optimistic UI: the switcher highlights immediately; the game rebuilds the session.
        host.shell = shell;
        if (isReplayMode()) {
            return;
        }
        void loadGame()
            .then(game => game.setSkin(host, shell))
            .catch((error: unknown) => {
                console.error('❌ Shell switch failed:', error);
            });
    };
    host.retry = () => {
        // recoverGame() contains its own failures; no rejection escapes here.
        void loadGame().then(game => game.recoverGame(host));
    };

    return { store, host };
}
